import { useCallback, useMemo, useState } from 'react';
import { Outlet } from '@tanstack/react-router';
import { MODULES } from '~/modules';
import { useAppearance, type Dock } from '~/platform/appearance/appearance';
import { useMediaQuery } from '~/platform/hooks/useMediaQuery';
import { resolveRegistry } from '~/platform/registry/registry';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { ToastViewport } from '~/design-system';
import { NavRail } from './NavRail';
import { TopBar } from './TopBar';
import { CommandPalette, usePaletteHotkey } from './CommandPalette';
import { RegistryContext } from './registryContext';
import './AppShell.css';

/* ------------------------------------------------------------------ *
 * The shell composes itself from the registry. There is no navigation
 * array and no route table in this file — adding a module changes
 * nothing here.
 * ------------------------------------------------------------------ */

export function AppShell() {
  const { business, can } = useBusiness();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const dock = useAppearance((s) => s.dock);
  // A phone is already bottom-docked: below 720px the stored dock is
  // ignored, the vertical markup renders and the mobile stylesheet
  // reshapes it into the bottom icon bar.
  const isNarrow = useMediaQuery('(max-width: 720px)');
  const effectiveDock: Dock = isNarrow ? 'left' : dock;

  const registry = useMemo(
    () => resolveRegistry({ all: MODULES, enabledModules: business.enabledModules, can }),
    [business.enabledModules, can],
  );

  usePaletteHotkey(useCallback(() => setPaletteOpen(true), []));

  return (
    <RegistryContext.Provider value={registry}>
      <div className="pd-shell">
        <NavRail registry={registry} dock={effectiveDock} />
        <TopBar onOpenSearch={() => setPaletteOpen(true)} />
        <main className="pd-shell__main">
          <Outlet />
        </main>
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} registry={registry} />
        <ToastViewport />
      </div>
    </RegistryContext.Provider>
  );
}
